import {
  doc, runTransaction, Timestamp, serverTimestamp, deleteDoc, updateDoc, getDoc,
} from "firebase/firestore";

export const RESERVATION_TTL_MS = 2 * 60 * 1000; // 2 minutes

// Persists across refresh and all tabs from the same browser (localStorage)
export const getOrCreateUserId = () => {
  try {
    let uid = localStorage.getItem("vbt_user_id");
    if (!uid) {
      uid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("vbt_user_id", uid);
    }
    return uid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Unique per browser tab; survives refresh within the same tab (sessionStorage)
export const getOrCreateTabId = () => {
  try {
    let tid = sessionStorage.getItem("vbt_tab_id");
    if (!tid) {
      tid = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("vbt_tab_id", tid);
    }
    return tid;
  } catch {
    return Math.random().toString(36).slice(2);
  }
};

// Deterministic doc ID — global across all job/opportunity applications for the same slot.
export const buildReservationDocId = (date, slotTime) =>
  `${date}_${slotTime.replace(/[: ]/g, "-")}`;

export const createSlotReservation = async (db, date, slotTime, userId, tabId) => {
  const docId = buildReservationDocId(date, slotTime);
  const reservationRef = doc(db, "slotReservations", docId);
  const now = new Date();
  const expiresAt = new Date(now.getTime() + RESERVATION_TTL_MS);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(reservationRef);

    if (snap.exists()) {
      const data = snap.data();
      const expired = data.expiresAt.toDate() <= now;
      // Lock ownership is per-tab, not per-user — two tabs of the same user are independent
      const isThisTab = data.tabId === tabId;

      if (data.status === "confirmed") throw new Error("SLOT_PERMANENTLY_TAKEN");
      // Block any tab that didn't create the lock, even if same userId
      if (!expired && !isThisTab) throw new Error("SLOT_TEMPORARILY_RESERVED");

      // This tab's own lock (or an expired one) — refresh/claim it
      transaction.update(reservationRef, {
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        updatedAt: serverTimestamp(),
      });
    } else {
      transaction.set(reservationRef, {
        interviewDate: date,
        slotTime,
        userId,
        tabId,
        status: "temporary",
        expiresAt: Timestamp.fromDate(expiresAt),
        createdAt: serverTimestamp(),
      });
    }
  });

  return { docId, expiresAt };
};

export const releaseReservation = (db, docId) =>
  deleteDoc(doc(db, "slotReservations", docId)).catch(() => {});

export const confirmReservation = (db, docId) =>
  updateDoc(doc(db, "slotReservations", docId), {
    status: "confirmed",
    confirmedAt: serverTimestamp(),
  }).catch(() => {});

// Restore an existing reservation for this user (any tab).
// Updates the tabId so the current tab becomes the new lock holder.
export const restoreReservation = async (db, docId, userId, tabId) => {
  try {
    const snap = await getDoc(doc(db, "slotReservations", docId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const expiresAt = data.expiresAt.toDate();
    if (data.userId !== userId || data.status !== "temporary" || expiresAt <= new Date()) return null;
    // Hand the lock to the current tab
    await updateDoc(doc(db, "slotReservations", docId), { tabId, updatedAt: serverTimestamp() });
    return { ...data, expiresAt, docId };
  } catch {
    return null;
  }
};
